import { useState, useEffect } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Monitor, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import PublicHeader from "./PublicHeader";
import PublicFooter, { SOCIAL_ICONS } from "./PublicFooter";

const inp = "bg-fx-base border-white/10 h-8 text-sm";
const move = (arr, i, d) => { const a = [...arr]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

function Row({ children, onUp, onDown, onDel, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      {children}
      <button onClick={onUp} className="p-1 text-fx-s3 hover:text-white"><ArrowUp className="h-3.5 w-3.5" /></button>
      <button onClick={onDown} className="p-1 text-fx-s3 hover:text-white"><ArrowDown className="h-3.5 w-3.5" /></button>
      <button onClick={onDel} className="p-1 text-neg hover:text-red-400" data-testid={`${testid}-delete`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="rounded-xl bg-fx-s1 border border-white/5 p-4 space-y-3">
      <div className="flex items-center justify-between"><h4 className="font-display font-bold text-sm">{title}</h4>{action}</div>
      {children}
    </div>
  );
}

function NavEditor({ L, set }) {
  const upd = (i, k, v) => set({ ...L, nav: L.nav.map((n, idx) => (idx === i ? { ...n, [k]: v } : n)) });
  return (
    <Section title="Navigation menu" action={<Button size="sm" onClick={() => set({ ...L, nav: [...L.nav, { label: "New link", url: "/", new_tab: false }] })} data-testid="layout-nav-add" className="h-7 bg-brandblue hover:bg-blue-500 gap-1"><Plus className="h-3.5 w-3.5" /> Link</Button>}>
      {L.nav.length === 0 && <p className="text-xs text-fx-s3">No menu links.</p>}
      {L.nav.map((n, i) => (
        <Row key={i} testid={`layout-nav-${i}`} onUp={() => set({ ...L, nav: move(L.nav, i, -1) })} onDown={() => set({ ...L, nav: move(L.nav, i, 1) })} onDel={() => set({ ...L, nav: L.nav.filter((_, x) => x !== i) })}>
          <Input value={n.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Label" data-testid={`layout-nav-${i}-label`} className={`${inp} w-32`} />
          <Input value={n.url} onChange={(e) => upd(i, "url", e.target.value)} placeholder="/path or https://" data-testid={`layout-nav-${i}-url`} className={`${inp} flex-1 font-mono text-xs`} />
          <label className="flex items-center gap-1 text-[10px] text-fx-s3 shrink-0"><Switch checked={n.new_tab} onCheckedChange={(v) => upd(i, "new_tab", v)} /> New tab</label>
        </Row>
      ))}
      <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-2 pt-2 border-t border-white/5">
        <label className="flex items-center gap-1.5 text-xs text-fx-s3"><Switch checked={L.header_cta.show_signin} onCheckedChange={(v) => set({ ...L, header_cta: { ...L.header_cta, show_signin: v } })} data-testid="layout-cta-signin" /> Sign in link</label>
        <Input value={L.header_cta.label} onChange={(e) => set({ ...L, header_cta: { ...L.header_cta, label: e.target.value } })} placeholder="Button text" data-testid="layout-cta-label" className={inp} />
        <Input value={L.header_cta.url} onChange={(e) => set({ ...L, header_cta: { ...L.header_cta, url: e.target.value } })} placeholder="Button link" data-testid="layout-cta-url" className={`${inp} font-mono text-xs`} />
      </div>
    </Section>
  );
}

function FooterEditor({ L, set }) {
  const cols = L.footer_columns;
  const setCols = (c) => set({ ...L, footer_columns: c });
  const updCol = (ci, patch) => setCols(cols.map((c, i) => (i === ci ? { ...c, ...patch } : c)));
  const updLink = (ci, li, k, v) => updCol(ci, { links: cols[ci].links.map((l, i) => (i === li ? { ...l, [k]: v } : l)) });
  return (
    <Section title="Footer columns" action={cols.length < 4 && <Button size="sm" onClick={() => setCols([...cols, { title: "New column", links: [] }])} data-testid="layout-col-add" className="h-7 bg-brandblue hover:bg-blue-500 gap-1"><Plus className="h-3.5 w-3.5" /> Column</Button>}>
      {cols.map((c, ci) => (
        <div key={ci} className="rounded-lg bg-fx-base border border-white/5 p-3 space-y-2" data-testid={`layout-col-${ci}`}>
          <Row testid={`layout-col-${ci}-row`} onUp={() => setCols(move(cols, ci, -1))} onDown={() => setCols(move(cols, ci, 1))} onDel={() => setCols(cols.filter((_, i) => i !== ci))}>
            <Input value={c.title} onChange={(e) => updCol(ci, { title: e.target.value })} placeholder="Column title" data-testid={`layout-col-${ci}-title`} className={`${inp} flex-1 font-semibold`} />
          </Row>
          {c.links.map((l, li) => (
            <Row key={li} testid={`layout-col-${ci}-link-${li}`} onUp={() => updCol(ci, { links: move(c.links, li, -1) })} onDown={() => updCol(ci, { links: move(c.links, li, 1) })} onDel={() => updCol(ci, { links: c.links.filter((_, i) => i !== li) })}>
              <Input value={l.label} onChange={(e) => updLink(ci, li, "label", e.target.value)} placeholder="Label" className={`${inp} w-36`} />
              <Input value={l.url} onChange={(e) => updLink(ci, li, "url", e.target.value)} placeholder="/path or https://" className={`${inp} flex-1 font-mono text-xs`} />
            </Row>
          ))}
          <button onClick={() => updCol(ci, { links: [...c.links, { label: "New link", url: "/" }] })} data-testid={`layout-col-${ci}-add-link`} className="text-xs text-brandblue hover:underline">+ Add link</button>
        </div>
      ))}
    </Section>
  );
}

function SocialEditor({ L, set }) {
  const used = L.socials.map((s) => s.platform);
  const free = Object.keys(SOCIAL_ICONS).filter((p) => !used.includes(p));
  const upd = (i, k, v) => set({ ...L, socials: L.socials.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) });
  return (
    <Section title="Social links" action={free.length > 0 && <Button size="sm" onClick={() => set({ ...L, socials: [...L.socials, { platform: free[0], url: "https://" }] })} data-testid="layout-social-add" className="h-7 bg-brandblue hover:bg-blue-500 gap-1"><Plus className="h-3.5 w-3.5" /> Social</Button>}>
      {L.socials.length === 0 && <p className="text-xs text-fx-s3">No social links.</p>}
      {L.socials.map((s, i) => (
        <Row key={i} testid={`layout-social-${i}`} onUp={() => set({ ...L, socials: move(L.socials, i, -1) })} onDown={() => set({ ...L, socials: move(L.socials, i, 1) })} onDel={() => set({ ...L, socials: L.socials.filter((_, x) => x !== i) })}>
          <select value={s.platform} onChange={(e) => upd(i, "platform", e.target.value)} data-testid={`layout-social-${i}-platform`} className="h-8 rounded-md bg-fx-base border border-white/10 px-2 text-sm capitalize w-32">
            {[s.platform, ...free].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Input value={s.url} onChange={(e) => upd(i, "url", e.target.value)} placeholder="https://" data-testid={`layout-social-${i}-url`} className={`${inp} flex-1 font-mono text-xs`} />
        </Row>
      ))}
    </Section>
  );
}

export default function SiteLayoutEditor() {
  const { layout: live, setLayout } = useSettings();
  const [L, setL] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings/layout").then((r) => { setL(r.data); setSaved(r.data); }); }, []);
  if (!L) return <p className="text-sm text-fx-s3">Loading…</p>;
  const dirty = JSON.stringify(L) !== JSON.stringify(saved);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/settings/layout", L); setSaved(r.data); setL(r.data); setLayout(r.data); toast.success("Header & footer published"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    setBusy(false);
  };
  return (
    <div className="grid xl:grid-cols-[minmax(420px,520px)_1fr] gap-5" data-testid="site-layout-editor">
      <div className="space-y-4">
        <NavEditor L={L} set={setL} />
        <FooterEditor L={L} set={setL} />
        <SocialEditor L={L} set={setL} />
        <Section title="Footer text">
          <div className="space-y-1"><Label className="text-xs">Description</Label><Textarea value={L.footer_description} onChange={(e) => setL({ ...L, footer_description: e.target.value })} data-testid="layout-footer-description" className="bg-fx-base border-white/10 min-h-[60px] text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs">Copyright</Label><Input value={L.copyright} onChange={(e) => setL({ ...L, copyright: e.target.value })} data-testid="layout-copyright" className={inp} /></div>
          <div className="space-y-1"><Label className="text-xs">Disclaimer</Label><Textarea value={L.footer_text} onChange={(e) => setL({ ...L, footer_text: e.target.value })} data-testid="layout-footer-text" className="bg-fx-base border-white/10 min-h-[60px] text-sm" /></div>
        </Section>
        <div className="flex gap-2 sticky bottom-2">
          <Button onClick={save} disabled={busy || !dirty} data-testid="layout-save" className="flex-1 bg-brandblue hover:bg-blue-500 gap-2"><Save className="h-4 w-4" /> {dirty ? "Publish changes" : "All changes published"}</Button>
          <Button onClick={() => setL(saved)} disabled={!dirty} variant="outline" data-testid="layout-reset" className="border-white/10 bg-fx-base gap-1"><RotateCcw className="h-4 w-4" /> Discard</Button>
        </div>
      </div>
      <div className="rounded-xl border border-white/10 overflow-hidden bg-white self-start xl:sticky xl:top-20" data-testid="layout-preview">
        <div className="flex items-center gap-2 px-3 py-2 bg-fx-s2 text-xs text-fx-s3"><Monitor className="h-3.5 w-3.5" /> Live preview {dirty && <span className="text-gold">· unpublished</span>}{!dirty && live && <span className="text-pos">· live</span>}</div>
        <div className="pointer-events-none select-none" style={{ zoom: 0.62 }}>
          <PublicHeader layout={L} preview />
          <div className="h-40 bg-slate-900 grid place-items-center text-slate-400 text-sm font-mono">Page content</div>
          <PublicFooter layout={L} preview />
        </div>
      </div>
    </div>
  );
}
